import React, { useMemo, useState } from 'react';

import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';

import { useCopyPackageInclusionsOrder } from '~/hooks/apis/inclusions/useCopyPackageInclusionsOrder';
import { useGetInclusionsCategories } from '~/hooks/apis/inclusions/useGetInclusionsCategories';

import { type PackageInclusionRecord, getInclusionCategory, isInclusionHidden } from '../../../utils';
import {
  type PackageInclusionsFieldName,
  getPackageInclusions,
  getPackageInclusionsFieldName,
  getPackageName,
} from '../../utils';

import PackageInclusionsTabRow from './PackageInclusionsTabRow';
import { PACKAGE_INCLUSIONS_GRID_COLUMNS } from './utils';

interface Props {
  offerId?: string;
  offerPackage: App.Package;
  packages: Array<App.Package>;
  vendorId?: string;
  onChange: (nextPackageValues: Partial<App.Package> & Record<string, unknown>) => void;
}

const FALLBACK_CATEGORY_OPTION = 'Other';
const PACKAGE_COLUMNS: Array<GridColDef<CopyTargetPackageRow>> = [
  {
    disableColumnMenu: true,
    field: 'name',
    headerName: 'Package',
    flex: 2,
    minWidth: 240,
    display: 'flex',
  },
  {
    disableColumnMenu: true,
    field: 'id',
    filterable: false,
    headerName: 'Package ID',
    sortable: false,
    flex: 1,
    minWidth: 180,
    display: 'flex',
  },
];

interface CopyTargetPackageRow {
  id: string;
  name: string;
}

function isInclusionRecord(value: unknown): value is PackageInclusionRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normaliseInclusion(value: unknown): PackageInclusionRecord {
  if (isInclusionRecord(value)) {
    return value;
  }

  if (typeof value === 'string') {
    return { name: value };
  }

  return {};
}

function getInclusionRowId(inclusion: PackageInclusionRecord, index: number) {
  const recordId = inclusion.id ?? inclusion.inclusion_id ?? inclusion.inclusion?.id;

  return recordId ? `inclusion:${recordId}:${index}` : `inclusion-index:${index}`;
}

function createInclusion(): PackageInclusionRecord {
  return {
    id: `local-inclusion-${crypto.randomUUID()}`,
    inclusion: {
      name: '',
      is_hidden: false,
      category: {
        name: 'Other',
      },
    },
    lux_plus_tier: null,
    style: null,
  };
}

function getInclusionPersistId(inclusion: PackageInclusionRecord) {
  const inclusionId = inclusion.inclusion_id ?? inclusion.inclusion?.id;

  return inclusionId ? String(inclusionId) : undefined;
}

function getPackageCopyOrderId(offerPackage: App.Package) {
  return offerPackage.id_salesforce_external ? String(offerPackage.id_salesforce_external) : undefined;
}

export default function PackageInclusionsTab({ offerId, offerPackage, packages, vendorId, onChange }: Props) {
  const [showHidden, setShowHidden] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedPackageIds, setSelectedPackageIds] = useState<GridRowSelectionModel>([]);
  const copyPackageInclusionsOrder = useCopyPackageInclusionsOrder();
  const { enqueueSnackbar } = useSnackbar();
  const { data: inclusionCategories = [] } = useGetInclusionsCategories();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const currentPackageId = getPackageCopyOrderId(offerPackage);
  const inclusionsFieldName: PackageInclusionsFieldName = getPackageInclusionsFieldName(offerPackage);
  const inclusions = useMemo(() => getPackageInclusions(offerPackage).map(normaliseInclusion), [offerPackage]);
  const inclusionRows = useMemo(
    () =>
      inclusions.map((inclusion, index) => ({
        inclusion,
        rowId: getInclusionRowId(inclusion, index),
      })),
    [inclusions],
  );
  const visibleRows = useMemo(
    () => (showHidden ? inclusionRows : inclusionRows.filter(({ inclusion }) => !isInclusionHidden(inclusion))),
    [inclusionRows, showHidden],
  );
  const visibleRowIds = useMemo(() => visibleRows.map(({ rowId }) => rowId), [visibleRows]);
  const categoryOptions = useMemo(() => {
    const fetchedCategories = inclusionCategories.map(({ category }) => category);
    const packageCategories = inclusions.map(getInclusionCategory).filter((category): category is string => !!category);

    return Array.from(new Set(fetchedCategories.concat(packageCategories, FALLBACK_CATEGORY_OPTION)));
  }, [inclusionCategories, inclusions]);
  const copyTargetPackageRows = useMemo<Array<CopyTargetPackageRow>>(
    () =>
      packages.reduce<Array<CopyTargetPackageRow>>((targetPackages, packageOption) => {
        const packageId = getPackageCopyOrderId(packageOption);

        if (!packageId || packageId === currentPackageId) {
          return targetPackages;
        }

        return targetPackages.concat({
          id: packageId,
          name: getPackageName(packageOption),
        });
      }, []),
    [currentPackageId, packages],
  );
  const canCopyInclusionsOrder =
    !!offerId && !!vendorId && !!currentPackageId && inclusions.length > 0 && copyTargetPackageRows.length > 0;

  const updateInclusions = (nextInclusions: Array<PackageInclusionRecord>) => {
    onChange({ [inclusionsFieldName]: nextInclusions });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldVisibleIndex = visibleRowIds.indexOf(String(active.id));
    const newVisibleIndex = visibleRowIds.indexOf(String(over.id));

    if (oldVisibleIndex === -1 || newVisibleIndex === -1) {
      return;
    }

    const reorderedVisibleRows = arrayMove(visibleRows, oldVisibleIndex, newVisibleIndex);
    const nextVisibleRows = [...reorderedVisibleRows];
    const nextInclusions = inclusionRows.map((row) => {
      if (showHidden || !isInclusionHidden(row.inclusion)) {
        return nextVisibleRows.shift()?.inclusion ?? row.inclusion;
      }

      return row.inclusion;
    });

    updateInclusions(nextInclusions.map((inclusion, index) => ({ ...inclusion, sort_order: index + 1 })));
  };

  const handleRowChange = (rowId: string, nextInclusion: PackageInclusionRecord) => {
    updateInclusions(
      inclusions.map((inclusion, index) => (getInclusionRowId(inclusion, index) === rowId ? nextInclusion : inclusion)),
    );
  };

  const handleDelete = (rowId: string) => {
    updateInclusions(inclusions.filter((inclusion, index) => getInclusionRowId(inclusion, index) !== rowId));
  };

  const handleCopyDialogClose = () => {
    if (!copyPackageInclusionsOrder.isPending) {
      setCopyDialogOpen(false);
      setSelectedPackageIds([]);
    }
  };

  const handleCopyInclusionsOrder = async () => {
    if (!offerId || !vendorId || !currentPackageId) {
      enqueueSnackbar('Unable to copy inclusions order for this package.', { variant: 'warning' });
      return;
    }

    const packageInclusions = inclusions
      .map((inclusion, index) => {
        const inclusionId = getInclusionPersistId(inclusion);

        return inclusionId
          ? {
              inclusion_id: inclusionId,
              sort_order: index + 1,
            }
          : undefined;
      })
      .filter((inclusion): inclusion is { inclusion_id: string; sort_order: number } => !!inclusion);

    if (packageInclusions.length === 0) {
      enqueueSnackbar('No saved inclusions are available to copy.', { variant: 'warning' });
      return;
    }

    try {
      await copyPackageInclusionsOrder.mutateAsync({
        offerId,
        vendorId,
        packageIds: Array.from(new Set(selectedPackageIds.concat(currentPackageId).map(String))),
        packageInclusions,
      });
      enqueueSnackbar('Copy inclusions order success', { variant: 'success' });
      setCopyDialogOpen(false);
      setSelectedPackageIds([]);
    } catch (error) {
      enqueueSnackbar(`Failed to copy inclusions order to packages: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Drag <DragIndicatorIcon fontSize="inherit" sx={{ verticalAlign: 'text-bottom' }} /> to reorder inclusions.
      </Typography>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            bgcolor: 'grey.50',
            display: { xs: 'none', lg: 'grid' },
            gap: 2,
            gridTemplateColumns: PACKAGE_INCLUSIONS_GRID_COLUMNS,
            px: 2,
            py: 1.5,
          }}
        >
          <Box />
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            Category
          </Typography>
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            Inclusion
          </Typography>
          <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
            Highlight
          </Typography>
          <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
            LuxPlus only
          </Typography>
          <Box />
        </Box>

        {visibleRows.length > 0 ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={visibleRowIds} strategy={verticalListSortingStrategy}>
              {visibleRows.map(({ inclusion, rowId }) => (
                <PackageInclusionsTabRow
                  key={rowId}
                  id={rowId}
                  categoryOptions={categoryOptions}
                  inclusion={inclusion}
                  onChange={(nextInclusion) => handleRowChange(rowId, nextInclusion)}
                  onDelete={() => handleDelete(rowId)}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No inclusions added.
            </Typography>
          </Box>
        )}
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={() => updateInclusions(inclusions.concat(createInclusion()))}
        >
          Add inclusion
        </Button>
        <Button variant="text" onClick={() => setShowHidden((currentValue) => !currentValue)}>
          {showHidden ? 'Hide hidden' : 'Show hidden'}
        </Button>
        <Button
          variant="text"
          href={vendorId ? `/vendors/${vendorId}/inclusions` : undefined}
          target="_blank"
          rel="noreferrer"
          disabled={!vendorId}
        >
          Vendor inclusions
        </Button>
        <Button variant="text" disabled={!canCopyInclusionsOrder} onClick={() => setCopyDialogOpen(true)}>
          Copy inclusions ordering
        </Button>
      </Stack>

      <Dialog open={copyDialogOpen} onClose={handleCopyDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Select packages to copy inclusions order to</DialogTitle>
        <Box sx={{ position: 'relative' }}>
          {copyPackageInclusionsOrder.isPending && (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }} />
          )}
        </Box>
        <DialogContent dividers>
          <DataGrid
            autoHeight
            checkboxSelection
            columns={PACKAGE_COLUMNS}
            density="compact"
            disableRowSelectionOnClick
            rows={copyTargetPackageRows}
            hideFooter
            onRowSelectionModelChange={setSelectedPackageIds}
            rowSelectionModel={selectedPackageIds}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={copyPackageInclusionsOrder.isPending} onClick={handleCopyDialogClose}>
            Close
          </Button>
          <Button
            disabled={selectedPackageIds.length === 0 || copyPackageInclusionsOrder.isPending}
            variant="contained"
            onClick={handleCopyInclusionsOrder}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
